/**
 * Clima vía wttr.in (servicio público, sin API key ni registro).
 * Documentación: https://github.com/chubin/wttr.in
 *
 * - Rango del día: `mintempC` / `maxtempC` del primer día en `weather[]` (pronóstico).
 * - Actual: `temp_C` / `FeelsLikeC` en `current_condition` (respaldo o detalle).
 *
 * Coordenadas: geocoding Open-Meteo solo para lat/lon del municipio en el Cesar.
 */

const GEOCODING_URL = 'https://geocoding-api.open-meteo.com/v1/search';
const WTTR_BASE = 'https://wttr.in';

/**
 * @param {string} municipalityName
 * @returns {Promise<{ latitude: number, longitude: number } | null>}
 */
async function geocodeMunicipioCesar(municipalityName) {
    const name = (municipalityName || '').trim();
    if (!name) return null;

    const params = new URLSearchParams({
        name,
        countryCode: 'CO',
        count: '15',
        language: 'es'
    });
    const res = await fetch(`${GEOCODING_URL}?${params}`);
    if (!res.ok) return null;
    const data = await res.json();
    const results = data.results;
    if (!Array.isArray(results) || results.length === 0) return null;

    const inCesar = results.find(
        (r) => r.country_code === 'CO' && r.admin1 === 'Cesar'
    );
    if (inCesar) {
        return { latitude: inCesar.latitude, longitude: inCesar.longitude };
    }

    return null;
}

function parseWttrCurrent(data) {
    const cc = data.current_condition && data.current_condition[0];
    const day = data.weather && data.weather[0];
    if (!cc) return null;

    const temp = Number.parseFloat(cc.temp_C);
    const feels = Number.parseFloat(cc.FeelsLikeC);
    const langEs = cc.lang_es && cc.lang_es[0] && cc.lang_es[0].value;
    const descEn = cc.weatherDesc && cc.weatherDesc[0] && cc.weatherDesc[0].value;
    const label = langEs || descEn || 'Condición desconocida';

    const code = Number.parseInt(cc.weatherCode, 10);

    const minC = day ? Number.parseFloat(day.mintempC) : NaN;
    const maxC = day ? Number.parseFloat(day.maxtempC) : NaN;
    const hasRange =
        Number.isFinite(minC) &&
        Number.isFinite(maxC) &&
        minC <= maxC;

    return {
        temperature: Number.isFinite(temp) ? Math.round(temp * 10) / 10 : 0,
        apparentTemperature: Number.isFinite(feels)
            ? Math.round(feels * 10) / 10
            : Number.isFinite(temp)
              ? Math.round(temp * 10) / 10
              : 0,
        /** Mínima prevista para el día (°C), si el API la envía. */
        tempMin: hasRange ? Math.round(minC) : null,
        /** Máxima prevista para el día (°C). */
        tempMax: hasRange ? Math.round(maxC) : null,
        weathercode: Number.isFinite(code) ? code : 0,
        label
    };
}

/**
 * @param {string} municipalityName
 * @returns {Promise<{ temperature: number, apparentTemperature: number, tempMin: number|null, tempMax: number|null, weathercode: number, label: string } | null>}
 */
async function getCurrentWeatherMunicipioCesar(municipalityName) {
    const coords = await geocodeMunicipioCesar(municipalityName);
    if (!coords) return null;

    const lat = coords.latitude.toFixed(4);
    const lon = coords.longitude.toFixed(4);
    const url = `${WTTR_BASE}/~${lat},${lon}?format=j1&lang=es`;

    const res = await fetch(url, { cache: 'no-store' });
    if (!res.ok) return null;

    const data = await res.json();
    return parseWttrCurrent(data);
}

export { geocodeMunicipioCesar, getCurrentWeatherMunicipioCesar };
